import { useMemo, type ReactElement } from 'react';
import { SuiteExecutionsTable } from '@/features/executions/components/SuiteExecutionsTable';
import { SuitesTable } from '@/features/suites/components/SuitesTable';
import { gridQueryKey } from '@/shared/hooks/queryKeys';
import { r, s } from '@/theme/tokens';
import { buildLocalQueryResult } from '@/ui/composites/grid/localGridUtils';
import { Box, Text } from '@/ui/primitives';
import { Tab, Tabs } from '@/ui/primitives/Tabs';
import { landingCopy } from './landingCopy';
import { suiteExecutionsRows, suitesTableRows } from './landingData';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, SuiteExecutionDto, SuiteListItemDto } from '@flowqa/shared';

type ExecutionTablesCarouselSectionProps = {
  width: number;
  carouselIndex: number;
  direction: 'next' | 'prev';
  onPrev: () => void;
  onNext: () => void;
  onSelect: (index: number) => void;
  slides: Array<{ id: string }>;
  suiteExecutionsQuery: (input: GridQueryInput) => Promise<QueryResult<SuiteExecutionDto>>;
  suitesQuery: (input: GridQueryInput) => Promise<QueryResult<SuiteListItemDto>>;
};

export const ExecutionTablesCarouselSection = ({
  width,
  carouselIndex,
  onSelect,
  suiteExecutionsQuery,
  suitesQuery,
}: ExecutionTablesCarouselSectionProps): ReactElement => {
  const tabValue = carouselIndex === 0 ? 'suites' : 'suite-executions';
  const initialSuitesData = useMemo(
    () =>
      buildLocalQueryResult(suitesTableRows, {
        page: 0,
        pageSize: 5,
        sorts: [],
        filters: [],
      }),
    [],
  );
  const initialSuiteExecutionsData = useMemo(
    () =>
      buildLocalQueryResult(suiteExecutionsRows, {
        page: 0,
        pageSize: 5,
        sorts: [],
        filters: [],
      }),
    [],
  );
  return (
    <Box component="section" sx={{ display: { xs: 'none', sm: 'block' } }}>
      <Box sx={{ maxWidth: width, mx: 'auto', width: '100%' }}>
        <Box sx={{ mb: s('md'), textAlign: 'center' }}>
          <Text role="sectionTitle" component="h2" transform="none" mb="xs">
            {landingCopy.executionTables.heading}
          </Text>
          <Text role="bodyMuted">{landingCopy.executionTables.intro}</Text>
        </Box>
        <Box
          sx={{
            border: 'none',
            backgroundColor: 'transparent',
            borderRadius: r('none'),
            overflow: 'hidden',
            width: '100%',
            minWidth: 0,
            contain: 'layout paint',
            isolation: 'isolate',
          }}
        >
          <Box sx={{ px: s('lg'), py: s('lg'), display: 'grid', gap: s('md') }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Tabs
                value={tabValue}
                onChange={(_event, next) => {
                  onSelect(next === 'suite-executions' ? 1 : 0);
                }}
                aria-label="Landing tables tabs"
              >
                <Tab label="Suites" value="suites" />
                <Tab label="Suite Executions" value="suite-executions" />
              </Tabs>
            </Box>
            <Box sx={{ width: '100%', minWidth: 0 }}>
              <Box
                key={`landing-table-tab-${tabValue}`}
                sx={(theme) => ({
                  animation: `landing-tab-fade-in 160ms ${theme.app.motion.easing.standard}`,
                  '@keyframes landing-tab-fade-in': {
                    from: { opacity: 0.35 },
                    to: { opacity: 1 },
                  },
                })}
              >
                {tabValue === 'suites' ? (
                  <SuitesTable
                    queryKey={gridQueryKey('landing/suites')}
                    queryFn={suitesQuery}
                    initialData={initialSuitesData}
                    pageSizeOptions={[5]}
                    initialPageSize={5}
                    disablePageSizeSelect
                    hideFooter
                    disableColumnSorting
                    persistKey={null}
                    syncStateToUrl={false}
                    showLastUpdated={false}
                    showDeltas
                    tokenOverride="local"
                    disableLinks
                  />
                ) : (
                  <SuiteExecutionsTable
                    queryKey={gridQueryKey('landing/suite-executions')}
                    queryFn={suiteExecutionsQuery}
                    initialData={initialSuiteExecutionsData}
                    pageSizeOptions={[5]}
                    initialPageSize={5}
                    disablePageSizeSelect
                    hideFooter
                    disableColumnSorting
                    persistKey={null}
                    syncStateToUrl={false}
                    showLastUpdated={false}
                    showExecutionRunColumn={false}
                    tokenOverride="local"
                    disableLinks
                    containerSx={{
                      width: '100%',
                      minWidth: 0,
                      maxWidth: '100%',
                      '& .MuiDataGrid-root': { width: '100%', minWidth: 0 },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
